import { useState } from 'react'
import { Save } from 'lucide-react'
import { useSettings, useUpdateSetting, useCreateSetting } from '@/hooks/useSettings'
import { Skeleton } from '@/components/ui'
import { toast } from 'sonner'

// ── Settings config ────────────────────────────────────────────────────────

interface SettingConfig {
  key: string
  label: string
  description: string
  placeholder: string
  sensitive?: boolean
}

interface SettingSection {
  title: string
  fields: SettingConfig[]
}

const SECTIONS: SettingSection[] = [
  {
    title: 'Infrastructure',
    fields: [
      {
        key: 'base_domain',
        label: 'Base Domain',
        description: 'Base domain used for tenant subdomains.',
        placeholder: 'deconstraint.ai',
      },
      {
        key: 'docr_registry',
        label: 'DOCR Registry',
        description: 'DigitalOcean Container Registry name.',
        placeholder: 'deconstraint',
      },
      {
        key: 'default_region',
        label: 'Default Region',
        description: 'Default DigitalOcean region for new droplets.',
        placeholder: 'nyc3',
      },
      {
        key: 'default_droplet_size',
        label: 'Default Droplet Size',
        description: 'Default droplet size slug.',
        placeholder: 's-2vcpu-4gb',
      },
      {
        key: 'golden_image_id',
        label: 'Golden Image ID',
        description: 'DigitalOcean snapshot ID used as the base image.',
        placeholder: '123456789',
      },
      {
        key: 'vpc_id',
        label: 'VPC ID',
        description: 'VPC UUID for tenant droplets.',
        placeholder: '',
      },
    ],
  },
  {
    title: 'Health Monitoring',
    fields: [
      {
        key: 'health_check_interval_seconds',
        label: 'Health Check Interval',
        description: 'Seconds between health checks (default: 5).',
        placeholder: '5',
      },
    ],
  },
]

// ── Skeleton section ───────────────────────────────────────────────────────

function SkeletonSection() {
  return (
    <div style={{ marginBottom: 32 }}>
      <Skeleton width={120} height={11} style={{ marginBottom: 16 }} />
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 24 }}>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i}>
            <Skeleton width={140} height={13} style={{ marginBottom: 6 }} />
            <Skeleton width={200} height={11} style={{ marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <Skeleton height={38} style={{ flex: 1 }} />
              <Skeleton width={72} height={38} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Settings Page ──────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { data: settings = [], isLoading } = useSettings()
  const updateSetting = useUpdateSetting()
  const createSetting = useCreateSetting()

  const [values, setValues] = useState<Record<string, string>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const getValue = (key: string) => {
    if (values[key] !== undefined) return values[key]
    const setting = settings.find(s => s.key === key)
    return setting?.value ?? ''
  }

  const handleChange = (key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async (key: string) => {
    const value = getValue(key)
    const existing = settings.find(s => s.key === key)

    setSavingKey(key)
    try {
      if (existing) {
        await updateSetting.mutateAsync({ id: existing.id, data: { value } })
      } else {
        await createSetting.mutateAsync({ key, value, description: '' })
      }
      toast.success(`Saved ${key}`)
    } catch {
      toast.error(`Failed to save ${key}`)
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <div style={{ maxWidth: 640 }}>
          {isLoading ? (
            <>
              <SkeletonSection />
              <SkeletonSection />
            </>
          ) : (
            SECTIONS.map(section => (
              <div key={section.title} style={{ marginBottom: 32 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  paddingBottom: 10, borderBottom: '1px solid var(--border-subtle)',
                  marginBottom: 20, userSelect: 'none',
                }}>
                  {section.title}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {section.fields.map(config => (
                    <SettingField
                      key={config.key}
                      label={config.label}
                      description={config.description}
                      value={getValue(config.key)}
                      placeholder={config.placeholder}
                      sensitive={config.sensitive}
                      saving={savingKey === config.key}
                      onChange={value => handleChange(config.key, value)}
                      onSave={() => handleSave(config.key)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ── Setting Field ──────────────────────────────────────────────────────────

function SettingField({
  label, description, value, placeholder, sensitive, saving, onChange, onSave,
}: {
  label: string; description: string; value: string; placeholder: string
  sensitive?: boolean; saving?: boolean; onChange: (v: string) => void; onSave: () => void
}) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
        {label}
      </label>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 10px 0', lineHeight: 1.4 }}>
        {description}
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <input
          className="input-field"
          type={sensitive ? 'password' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1, padding: '9px 12px',
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13, color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
            outline: 'none',
          }}
        />
        <button
          className="btn-primary"
          onClick={onSave}
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 16px',
            backgroundColor: 'var(--brand)',
            border: 'none', borderRadius: 'var(--radius-md)',
            fontSize: 13, fontWeight: 600, color: '#fff',
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.6 : 1, whiteSpace: 'nowrap',
          }}
        >
          <Save size={14} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
